import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { 
  Bell, Send, Loader2, Settings, DollarSign, Vote, 
  User, AlertCircle, CheckCircle2, Clock
} from 'lucide-react';
import { toast } from 'sonner';

const reminderTypeLabels = {
  all: 'Todos',
  payment: 'Pagamentos',
  vote: 'Votações',
};

export function RemindersManager({ tripId }) {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [pendingItems, setPendingItems] = useState([]);
  const [config, setConfig] = useState({
    enabled: true,
    days_before_due: 3,
    reminder_type: 'all'
  });
  const [savingConfig, setSavingConfig] = useState(false);

  useEffect(() => {
    fetchData();
  }, [tripId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [pendingRes, configRes] = await Promise.all([
        api.get(`/trips/${tripId}/reminders/pending`),
        api.get(`/trips/${tripId}/reminders/config`)
      ]);
      setPendingItems(pendingRes.data);
      setConfig(configRes.data);
    } catch (error) {
      console.error('Error fetching reminders data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendBatch = async (type = 'all') => {
    setSending(true);
    try {
      const response = await api.post(`/trips/${tripId}/reminders/send-batch?reminder_type=${type}`);
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error('Erro ao enviar lembretes');
    } finally {
      setSending(false);
    }
  };

  const handleUpdateConfig = async (updates) => {
    setSavingConfig(true);
    try {
      const newConfig = { ...config, ...updates };
      await api.put(`/trips/${tripId}/reminders/config`, newConfig);
      setConfig(newConfig);
      toast.success('Configuração salva');
    } catch (error) {
      toast.error('Erro ao salvar configuração');
    } finally {
      setSavingConfig(false);
    }
  };

  const paymentReminders = pendingItems.filter(item => item.type === 'payment');
  const voteReminders = pendingItems.filter(item => item.type === 'vote');

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Config Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5 text-orange-500" />
            Configurações de Lembretes
          </CardTitle>
          <CardDescription>
            Configure quando e como enviar lembretes automáticos
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label className="font-medium">Lembretes ativados</Label>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Permitir envio de lembretes para membros
              </p>
            </div>
            <Switch
              checked={config.enabled}
              onCheckedChange={(checked) => handleUpdateConfig({ enabled: checked })}
              disabled={savingConfig}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Dias antes do vencimento</Label>
              <Select
                value={config.days_before_due.toString()}
                onValueChange={(v) => handleUpdateConfig({ days_before_due: parseInt(v) })}
                disabled={savingConfig}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 dia</SelectItem>
                  <SelectItem value="2">2 dias</SelectItem>
                  <SelectItem value="3">3 dias</SelectItem>
                  <SelectItem value="5">5 dias</SelectItem>
                  <SelectItem value="7">7 dias</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Tipo de lembrete</Label>
              <Select
                value={config.reminder_type}
                onValueChange={(v) => handleUpdateConfig({ reminder_type: v })}
                disabled={savingConfig}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="payment">Apenas pagamentos</SelectItem>
                  <SelectItem value="vote">Apenas votações</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Pending Items Summary */}
      <div className="grid grid-cols-2 gap-4">
        <Card className={paymentReminders.length > 0 ? 'border-orange-200' : ''}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Pagamentos pendentes</p>
                  <p className="font-bold text-xl text-slate-900 dark:text-white">{paymentReminders.length}</p>
                </div>
              </div>
              {paymentReminders.length > 0 && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleSendBatch('payment')}
                  disabled={sending}
                >
                  <Send className="w-4 h-4 mr-1" />
                  Enviar
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className={voteReminders.length > 0 ? 'border-purple-200' : ''}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                  <Vote className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Votos pendentes</p>
                  <p className="font-bold text-xl text-slate-900 dark:text-white">{voteReminders.length}</p>
                </div>
              </div>
              {voteReminders.length > 0 && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleSendBatch('vote')}
                  disabled={sending}
                >
                  <Send className="w-4 h-4 mr-1" />
                  Enviar
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Send All Button */}
      {pendingItems.length > 0 && (
        <Button
          className="w-full bg-orange-500 hover:bg-orange-600"
          onClick={() => handleSendBatch('all')}
          disabled={sending}
          data-testid="send-all-reminders-btn"
        >
          {sending ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Bell className="w-4 h-4 mr-2" />
          )}
          Enviar todos os lembretes ({pendingItems.length})
        </Button>
      )}

      {/* Detailed List */}
      {pendingItems.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Pendências Detalhadas</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 max-h-[400px] overflow-y-auto">
              {pendingItems.map((item, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                      item.type === 'payment' 
                        ? 'bg-orange-100 text-orange-600' 
                        : 'bg-purple-100 text-purple-600'
                    }`}>
                      {item.type === 'payment' ? (
                        <DollarSign className="w-4 h-4" />
                      ) : (
                        <Vote className="w-4 h-4" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-slate-900 dark:text-white text-sm">{item.title}</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        <User className="w-3 h-3 inline mr-1" />
                        {item.member_name}
                        {item.amount && (
                          <span className="ml-2">• R$ {item.amount.toFixed(2)}</span>
                        )}
                      </p>
                    </div>
                  </div>
                  <Badge variant="outline" className={
                    item.type === 'payment' ? 'text-orange-600' : 'text-purple-600'
                  }>
                    {item.type === 'payment' ? 'Pagamento' : 'Votação'}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {pendingItems.length === 0 && (
        <Card className="border-dashed">
          <CardContent className="py-8 text-center">
            <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-4" />
            <h3 className="font-medium text-slate-900 dark:text-white mb-2">Tudo em dia!</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Não há pendências para enviar lembretes
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default RemindersManager;
